import { useState } from 'react';
import {
  WIN_CONDITIONS,
  deckLabel,
  isDraft,
  winConditionLabel,
  type WinCondition,
} from '@edh/shared';
import { RankedAvatar } from './RankedAvatar';
import { Sheet } from './Sheet';
import {
  damageLogOf,
  standingOf,
  takedownsOf,
  turnStepsOf,
  type LiveGame,
  type LiveView,
} from '@edh/shared';
import { finishGame } from '../lib/live';
import { useLookups, useStore } from '../lib/store';

/**
 * Finishing a game: who won, and anything worth remembering about it.
 *
 * The turn count and everyone's remaining life are already known — that is the
 * entire payoff for the counter having been on all evening. Nobody types them
 * in, and margin of victory gets a real number instead of a guess.
 */
/** 1st, 2nd, 3rd, 4th — the only four this ever needs. */
const ordinal = (n: number): string => ['—', '1st', '2nd', '3rd', '4th'][n] ?? `${n}th`;

export function EndGame({
  game,
  view,
  onCancel,
  onFinished,
}: {
  game: LiveGame;
  /** Life, poison and who went out, replayed from the logs. */
  view: LiveView;
  onCancel: () => void;
  onFinished: () => void;
}) {
  const { logMatch } = useStore();
  const { pilot, deck } = useLookups();
  /**
   * Opens on whoever is still standing with the most life.
   *
   * Almost always right — the game usually ends because everyone else is dead,
   * and when it ends to a concede the healthiest player is still the likeliest
   * answer. It stays a guess, though, so it is preselected rather than assumed:
   * a combo kill from four life is exactly the game somebody would be recording
   * here, and it is one tap to correct.
   */
  const [winner, setWinner] = useState<string | null>(() => {
    /*
     * Anybody still in it, by either measure.
     *
     * `outAt` rather than the declaration, because a table that plays a game
     * out and never presses a button still knows who was standing at the end —
     * and offering somebody on zero life as the likely winner is exactly the
     * wrong guess to preselect.
     */
    const candidates = view.seats.filter((s) => s.outAt === null);
    const field = candidates.length ? candidates : view.seats;
    return field.reduce((best, s) => (s.life > best.life ? s : best)).pilotId;
  });
  const [winCondition, setWinCondition] = useState<WinCondition | ''>('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);

  /**
   * Everything the counter measured, which is true either way.
   *
   * Shared by confirming a winner and cancelling, because a cancelled game was
   * still played - the life went down, the turns were taken, the evening
   * happened. What a cancellation drops is the result: no winner, no finishing
   * order, no takedowns, and nothing paid. Those are added by `finish` alone.
   */
  function measured() {
    return {
      entries: game.seats.map((s) => ({ pilotId: s.pilotId!, deckId: s.deckId! })),
      // Free, because the counter was already tracking them.
      endTurn: view.turn,
      // The counter has held a start time since the game began; this is the
      // first thing that has ever asked it for one.
      durationSeconds: Math.max(
        0,
        Math.round((Date.now() - (game.startedAt ?? Date.now())) / 1000),
      ),
      lifeRemaining: Object.fromEntries(view.seats.map((s) => [s.pilotId!, s.life])),
      onThePlay: game.seats[game.startingSeat]?.pilotId ?? undefined,
      poison: Object.fromEntries(view.seats.map((s) => [s.pilotId!, s.poison])),
      poisonLog: damageLogOf(game, game.poisonLog ?? []),
      commanderDamage: Object.fromEntries(view.seats.map((s) => [s.pilotId!, s.commanderDamage])),
      note: note || undefined,
      // The odds as they stood at kick-off, not as they stand now.
      predicted: game.predicted,
      // What kind of game this was, so the history can say so.
      mode: (isDraft(game) ? 'draft' : 'standard') as 'draft' | 'standard',
      // Seat indices mean nothing once the game is over, so the log goes up
      // keyed by player.
      damageLog: damageLogOf(game, game.log ?? []),
      // Every turn the game actually had, so the life chart can be read a turn
      // at a time rather than a round at a time. Seats here, unlike everything
      // above, because they index the entries array.
      turnLog: turnStepsOf(game),
    };
  }

  /**
   * Close the table with no result.
   *
   * A misdeal, an argument nobody could settle, somebody having to leave. The
   * game is logged so the night reads correctly and nobody wonders whether they
   * forgot to write it down, and it moves no rating in either direction.
   */
  async function cancelGame() {
    if (busy) return;
    setBusy(true);
    try {
      const result = await logMatch({ ...measured(), winnerPilotId: null, cancelled: true });
      try {
        await finishGame(game.id, result.match.id);
      } catch {
        // Same as below: everyone has the match, only the ceremony is missed.
      }
      onFinished();
    } finally {
      setBusy(false);
    }
  }

  async function finish() {
    if (!winner || busy) return;
    setBusy(true);
    try {
      const result = await logMatch({
        ...measured(),
        winnerPilotId: winner,
        // Free, because the counter was already tracking them.
        endTurn: view.turn,
        // Second place is worth more than last, and this is the only record of
        // which was which. Built from the order people were eliminated, with
        // survivors tied — the game gave no way to separate them.
        standing: standingOf(view, winner),
        winCondition: winCondition || undefined,
        // Who knocked whom out, as the table confirmed it. Pays the killer in
        // a pod and nobody in a duel, where the only takedown is the win.
        takedowns: takedownsOf(view),
      });
      /*
       * Then tell the table which match it became.
       *
       * Not a delete. Deleting it here is what meant three people watched the
       * game vanish off their phones while only the one holding this sheet saw
       * a result — the others had nothing left to look at and nothing to look
       * it up by. Now the game stays as `done` with the match's id on it, every
       * phone finds it on its next poll, and the row is swept away when
       * somebody opens the next table.
       *
       * Second, and allowed to fail quietly: logging the match is queued and
       * survives being offline, this is not, and a stale game row is a nuisance
       * where a lost game is not.
       */
      try {
        await finishGame(game.id, result.match.id);
      } catch {
        // Everyone still has the match itself; only the ceremony is missed.
      }
      onFinished();
    } finally {
      setBusy(false);
    }
  }

  return (
    <Sheet title="Confirm winner" onClose={onCancel}>
      <div className="list" style={{ marginBottom: 14 }}>
        {view.seats.map((seat) => {
          const p = pilot(seat.pilotId ?? '');
          const d = deck(seat.deckId ?? '');
          const picked = winner === seat.pilotId;
          return (
            <button
              key={seat.pilotId}
              className="list-item"
              style={{
                border: 0,
                width: '100%',
                textAlign: 'left',
                cursor: 'pointer',
                background: picked ? 'color-mix(in srgb, var(--gold) 16%, var(--panel))' : undefined,
              }}
              onClick={() => setWinner(seat.pilotId)}
            >
              <span style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                <RankedAvatar pilot={p} size={30} />
                <span style={{ minWidth: 0 }}>
                  <span style={{ display: 'block' }}>{p?.name}</span>
                  <span className="sub">{d ? deckLabel(d) : ''}</span>
                </span>
              </span>
              <span className="num" style={{ color: seat.outAt !== null ? 'var(--muted)' : 'var(--text)' }}>
                {seat.life}
              </span>
            </button>
          );
        })}
      </div>

      <p className="hint" style={{ marginBottom: 14 }}>
        Ended on turn {view.turn}. Life totals are recorded as they stand.
      </p>

      {/* Shown rather than asked for. It is worked out from the order people
          went out, and a table that has just finished a game should not have to
          agree on a placing before it can pack up. */}
      {winner && view.seats.length > 2 && (
        <>
          <div className="section-head" style={{ marginTop: 0 }}>
            <h2>Finishing order</h2>
          </div>
          <div className="finish-order">
            {(() => {
              const standing = standingOf(view, winner);
              return [...view.seats]
                .sort((a, b) => standing[a.pilotId!]! - standing[b.pilotId!]!)
                .map((seat) => {
                  const place = standing[seat.pilotId!]!;
                  const out = seat.outAt !== null;
                  return (
                    <div className="finish-row" key={seat.pilotId}>
                      <span className="finish-place num">{ordinal(place)}</span>
                      <RankedAvatar pilot={pilot(seat.pilotId ?? '')} size={24} />
                      <span className="finish-name">{pilot(seat.pilotId ?? '')?.name}</span>
                      <span className="finish-why">
                        {place === 1
                          ? 'won'
                          : out
                            ? seat.outTurn
                              ? `out on turn ${seat.outTurn}`
                              : 'out'
                            : 'still standing'}
                      </span>
                    </div>
                  );
                });
            })()}
          </div>
          <p className="hint" style={{ margin: '6px 0 14px' }}>
            Later out places higher, and anyone still alive ties behind the winner. Second
            place loses fewer rating points than last.
          </p>
        </>
      )}

      <label className="field">
        <span>How it ended</span>
        <select
          className="select"
          value={winCondition}
          onChange={(e) => setWinCondition(e.target.value as WinCondition | '')}
        >
          <option value="">—</option>
          {WIN_CONDITIONS.map((value) => (
            <option key={value} value={value}>
              {winConditionLabel(value)}
            </option>
          ))}
        </select>
      </label>

      <label className="field">
        <span>Note</span>
        <input
          className="input"
          placeholder="lost to mana screw"
          maxLength={140}
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
      </label>

      <div className="btn-row">
        <button className="btn ghost" onClick={onCancel}>
          Keep playing
        </button>
        <button className="btn primary" disabled={!winner || busy} onClick={() => void finish()}>
          {busy ? <span className="spinner" /> : 'Confirm'}
        </button>
      </div>

      {/*
        Below the row and quieter than either, because it is neither of the two
        things somebody opening this sheet came here to do. Two taps, because it
        ends the game for everybody at the table and the only way back is a
        leader removing the row afterwards.
      */}
      <div className="section-head">
        <h2>No result</h2>
      </div>
      {confirmCancel ? (
        <>
          <p className="hint">
            The game is kept in the history so the night reads right, and moves nobody's
            rating in either direction.
          </p>
          <div className="btn-row">
            <button className="btn ghost" disabled={busy} onClick={() => setConfirmCancel(false)}>
              Never mind
            </button>
            <button className="btn danger" disabled={busy} onClick={() => void cancelGame()}>
              {busy ? <span className="spinner" /> : 'Cancel the game'}
            </button>
          </div>
        </>
      ) : (
        <button className="btn ghost block" disabled={busy} onClick={() => setConfirmCancel(true)}>
          Cancel the game instead
        </button>
      )}
    </Sheet>
  );
}
